/* eslint-disable react-hooks/exhaustive-deps */
import debounce from "lodash/debounce";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { usePostSync } from "../../../../api/mutations";
import {
  useGetMyAccount,
  useGetMyProfile,
  useGetMyWorkAutoSuggest,
} from "../../../../api/queries";
import useSiteConfigAuth from "../../../../api/sanity/queries/getSiteConfigWithCode";
import useSyncsQuery from "../../../../api/sanity/queries/getSyncs";
import FormError from "../../../../components/FormError";
import H2 from "../../../../components/H2";
import H4 from "../../../../components/H4";
import MultipleSelect from "../../../../components/MultipleSelect";
import RebrandPrimaryButton from "../../../../components/RebrandPrimaryButton";
import Select from "../../../../components/Select";
import Spacer from "../../../../components/Spacer";
import TextArea from "../../../../components/TextArea";
import { sendEvent } from "../../../../helpers/helpers";
// import AppState from "../../../../store/appstate";
import useAppStore from "../../../../store/appStateZustand";

function SyncsApply() {
  const { t } = useTranslation("other");
  const { id } = useParams();
  const [opp, setOpp] = useState(id || null);
  const [songList, setSongList] = useState([]); // Initialize as an empty array instead of undefined
  const [text, setText] = useState(""); // Initialize as empty string instead of undefined
  const [pending, setPending] = useState(false);
  const [mutationErrorMessage, setMutationErrorMessage] = useState("");
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [searchString, setSearchString] = useState("");
  const [options, setOptions] = useState([]);
  const [cachedSongs, setCachedSongs] = useState([]);
  const [noOptionsMessage, setNoOptionsMessage] = useState("Start typing to search...");
  const debouncedSearch = debounce((inputValue) => {
    if (inputValue.length > 0) {
      setSearchString(inputValue);
    }
  }, 500);

  const fetchOptions = useCallback((inputValue) => {
    debouncedSearch(inputValue);
  }, [debouncedSearch]);

  // const selectedAccount = AppState.useState(s => s.selectedAccount);
  const selectedAccount = useAppStore(s => s.selectedAccount);

  const works = useGetMyWorkAutoSuggest({
    aac: selectedAccount,
    searchstring: searchString,
  });

  const myAccount = useGetMyAccount({
    artistaccountcode: selectedAccount,
  });
  const userInfo = useGetMyProfile();
  const createMutation = usePostSync();

  const history = useHistory();
  const { data: siteConfig } = useSiteConfigAuth();
  const noSyncs = siteConfig?.disable_syncs;

  useEffect(() => {
    if (works && works?.data?.works) {
      const fetchedOptions = works?.data.works.map((songData) => {
        const songOption = {
          label: `${songData.title} - ${songData.performingartists}`,
          value: songData.workcode,
        };
        return songOption;
      });

      // Only update options if they've actually changed
      setOptions((prevOptions) => {
        const prevOptionsStr = JSON.stringify(prevOptions);
        const newOptionsStr = JSON.stringify(fetchedOptions);
        return prevOptionsStr !== newOptionsStr ? fetchedOptions : prevOptions;
      });

      // Update cached songs without duplicates
      setCachedSongs((prevCachedSongs) => {
        // Create a map of existing work codes for quick lookup
        const existingWorkCodes = new Set(prevCachedSongs.map(song => song.workcode));

        // Only add new songs that aren't already cached
        const newSongs = works.data.works.filter(song => !existingWorkCodes.has(song.workcode));

        if (newSongs.length === 0) {
          // No new songs to add, return previous array to prevent re-render
          return prevCachedSongs;
        }

        return [...prevCachedSongs, ...newSongs];
      });

      // Only update message if needed based on fetched options
      setNoOptionsMessage((prevMessage) => {
        const newMessage = fetchedOptions.length === 0 ? "No options found" : "Start typing to search...";
        return prevMessage !== newMessage ? newMessage : prevMessage;
      });
    }
  }, [works]);

  useEffect(() => {
    if (noSyncs) {
      history.push("/");
    }
  }, [noSyncs, history]);

  useEffect(() => {
    if (selectedAccount) {
      works.refetch();
    }
  }, [selectedAccount]);

  // const syncs = getSyncs();
  const { data: syncsData, isSuccess } = useSyncsQuery();

  const checkErrors = () => {
    const errs = {};
    let valid = true;
    if (!opp) {
      errs.opp = t("regex:choose-sync-error");
      valid = false;
    }

    // has at least one performer
    if (!songList || songList.length < 1) {
      errs.songList = t("regex:add-at-least-one-song-error");
      valid = false;
    }

    if (songList) {
      songList.forEach((sl) => {
        if (!sl.workcode) {
          errs.songList = t("regex:select-every-song-error");
          valid = false;
        }
      });
    }

    setErrors(errs);
    return valid;
  };

  const submitApplication = () => {
    setPending(true);
    setMutationErrorMessage("");
    setValidationErrorMessage("");
    const valid = checkErrors();

    if (valid) {
      const selectedWorkcodes = songList.map(s => s.workcode);
      const selectedSongs = selectedWorkcodes.map((workcode) => {
        const song = songList.find(s => s.workcode === workcode);
        const cachedSong = cachedSongs.find(
          option => option.workcode === workcode,
        );
        return {
          workcode,
          title: song.title || (cachedSong ? cachedSong.title : ""),
        };
      });

      const data = {
        type: "sync",
        songs: JSON.stringify(selectedSongs),
        content: text,
        opportunity: syncsData && opp
          ? syncsData.find(sync => Number.parseInt(sync._id) === Number.parseInt(opp))
            ?.name
          : "",
        username: userInfo.data.username,
        email: userInfo.data.email,
        artist: myAccount.data.accountname,
      };

      createMutation.mutate(data, {
        onSuccess: (response) => {
          if (response.failedvalidation) {
            setValidationErrorMessage(response.data.messages);
            setPending(false);
          }
          else {
            toast.success(t("other:successfully-submitted-sync-toast"), {
              position: "bottom-right",
              autoClose: 5000,
              hideProgressBar: true,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
            });
            history.push("/syncs");
            setPending(false);
            sendEvent({ property: "pe14489857_sync_brief_applied_for" });
          }
        },
        onError: (error) => {
          setMutationErrorMessage(error.message);
          setPending(false);
        },
      });
    }
    setPending(false);
  };

  return (
    <div className="flex-1 order-last xl:w-full bg-sentric-rebrand-light-grey min-h-50 lg:min-h-screen-minus-header">
      <H2>{t("other:apply-for-sync")}</H2>
      <Spacer />
      {isSuccess && (
        <div className="w-full lg:w-2/3">
          <H4>{t("other:which-sync-header")}</H4>
          <Spacer thin />
          <Select
            list
            id="selectSync"
            required
            className="text-base select-search font-sentric-alt"
            name="opp"
            placeholder={t("other:select-sync-placeholder")}
            value={opp}
            error={errors.opp}
            onChange={selected => setOpp(selected)}
            options={syncsData.map(sync => ({
              name: sync.name,
              value: sync._id,
            }))}
          />
          <Spacer />
          <H4>{t("other:select-one-song-header")}</H4>
          <Spacer thin />
          <MultipleSelect
            id="txtSyncAddSong"
            type="text"
            addAnotherText={t("other:add-another-song")}
            customLabel={t("other:select-a-song-placeholder")}
            valueKey="workcode"
            value={songList}
            onChange={(arr) => {
              // Only update if the array content has actually changed
              const currentSongListStr = JSON.stringify(songList || []);
              const newSongListStr = JSON.stringify(arr || []);

              if (currentSongListStr !== newSongListStr) {
                setSongList(arr);
              }
            }}
            error={errors.songList}
            options={options}
            onInputChange={fetchOptions}
            noOptionsMessage={noOptionsMessage}
          />
          <Spacer />
          <H4>{t("other:tell-us-more-header")}</H4>
          <Spacer thin />
          <TextArea
            id="txtSyncEvidence"
            className="w-full"
            type="textarea"
            error={errors.durationsecs}
            placeholder={t("other:supporting-text-placeholder")}
            value={text}
            onBlur={() => {}}
            onChange={(e) => {
              setText(e.target.value);
            }}
          />
          <Spacer />
          <FormError mutationError={mutationErrorMessage} validationError={validationErrorMessage} />
          <div className="flex justify-end w-full">
            <RebrandPrimaryButton
              id="btnSyncSubmit"
              pending={pending}
              onClick={submitApplication}
              arrow
            >
              {t("common:submit-button")}
            </RebrandPrimaryButton>
          </div>
          <Spacer />
        </div>
      )}
    </div>
  );
}
export default SyncsApply;
